import type { ItemType } from '@/config/itemTypes';

// Menu thumbnails live alongside the copied Dugout assets, under a `menu/` subdir of pre-recoloured
// files (see scripts/generate-dugout-menu-thumbnails.mjs).
const MENU_DIR = 'assets/dugout/menu/';
// The neutral fill for symbol (alternateIcon) mode — must match the generator's ALT_FILL literal.
const ALT_FILL = '333333';

const basename = (src: string): string =>
  src
    .split('/')
    .pop()
    ?.replace(/\.svg$/, '') ?? src;

// Colour → the 6-hex-digit filename token the generator used (config colours are already #RRGGBB, but
// tolerate #RGB defensively).
const hex = (color: string): string => {
  const c = color.replace('#', '');
  return (
    c.length === 3
      ? c
          .split('')
          .map((d) => d + d)
          .join('')
      : c
  ).toUpperCase();
};

// A menu shape is recoloured iff Dugout recolours it: the player shirts + the caps (`js-main-color`
// SVGs). `shirt-annotation` also contains "shirt" but is a Tool (never rendered through here); exclude
// it so a future caller can't accidentally look up a non-existent recoloured file.
const isRecoloured = (variation: string): boolean =>
  (variation.includes('shirt') && variation !== 'shirt-annotation') ||
  variation.includes('cone');

/**
 * The menu-thumbnail `<img>` src for a Dugout item. Dugout paints each thumbnail per team (its own tool
 * recolours a shared SVG at load); we point the `<img>` at a pre-recoloured file instead, so the menu
 * matches Dugout's colours while the DOM stays a plain `<img>` (identical in shape to every other theme).
 * In `alternateIcon` (symbol) mode every item shows its shape painted neutral #333; in `defaultIcon` mode
 * shirts/caps take their team `color` and goals/ball/pion keep their own artwork (returned unchanged).
 *
 * DUGOUT-ONLY: call sites gate on `isDugoutTheme`; the other themes render `item.defaultIcon.src` directly,
 * so this never runs for them (and their menu DOM is byte-identical to before).
 */
const dugoutMenuIcon = (item: ItemType, iconType: string): string => {
  if (iconType === 'alternateIcon' && item.alternateIcon?.src)
    return `${MENU_DIR}${basename(item.alternateIcon.src)}-${ALT_FILL}.svg`;
  if (isRecoloured(item.variation) && item.color)
    // Trainer/Scheidsrechter carry a letter-badge `button` (shirt-t/shirt-s) — Dugout recolours THAT,
    // not the plain shirt, so the dark badge keeps its white T/S. Every other shirt/cone has no
    // `button`, so this falls back to the shape's own artwork exactly as before.
    return `${MENU_DIR}${basename((item.button ?? item.defaultIcon).src)}-${hex(item.color)}.svg`;
  return item.defaultIcon.src;
};

export default dugoutMenuIcon;
